import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import Typography from 'components/Typography';
import Button from 'components/Button';
import classnames from 'classnames/bind';
import { removeCookie, getCookie } from 'utils/cookie';
import { updateUserProfile } from 'store/actions/user';
import { MODAL_STATE } from 'store/actions/modal';
import NameForm from './NameForm';
import PasswordForm from './PasswordForm';
import styles from './WelcomeBackModal.module.scss';
import { DASHBOARD_FULL } from 'routes';
import { notifyWithSnack } from 'store/actions/notifications';

const cx = classnames.bind(styles);

// Function to create the Yup schema based on whether password validation is needed
const createSchema = (isPasswordValidationFailed, first_name, last_name, email, title) => {
  let baseSchema = {};
  if (!first_name || !last_name) {
    baseSchema = {
      first_name: yup.string().required('First name is required.'),
      last_name: yup.string().required('Last name is required.'),
      email: yup.string().email('Please enter a valid email address.').required('Email is required.'),
      title: yup
        .string()
        .required('Title is required.')
        .test('is-empty', 'Title is required.', (value) => {
          return value !== undefined && value !== null && value !== '';
        }),
    };
  }
  if (isPasswordValidationFailed) {
    baseSchema.password = yup
      .string()
      .required('New password field is required.')
      .min(8, 'Password must be at least 8 characters.')
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*(\d|\W)).{8,}$/,
        'Passwords must include an upper and lowercase letter, and at least one number or special character.',
      );
    baseSchema.confirm_password = yup.string().oneOf([yup.ref('password'), null], 'Passwords must match.');
  }

  return yup.object().shape(baseSchema);
};

const ProfileForm = ({ onModalClose }) => {
  const [showNamePopup, setShowNamePopup] = useState(false);
  const [showPassPopup, setShowPassPopup] = useState(false);
  const [_isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [isPasswordValidationFailed, setIsPasswordValidationFailed] = useState(getCookie('password_validation_failed'));
  const { first_name, last_name, title, email } = useSelector(({ user }) => user);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const schema = createSchema(isPasswordValidationFailed, first_name, last_name, title, email);

  const { reset, ...formMethods } = useForm({ resolver: yupResolver(schema), mode: 'onChange' });

  const handleFormSubmit = (data) => {
    const paramsData = Object.keys(data).reduce((obj, key) => {
      obj[key] = data[key];
      return obj;
    }, {});
    const user_key = getCookie('user_token');

    dispatch(updateUserProfile(user_key, paramsData))
      .then(() => {
        setIsFormSubmitted(true);
        reset();
        setIsPasswordValidationFailed(false);
        removeCookie('password_validation_failed');
        dispatch(
          notifyWithSnack({
            message: `Your data has been updated successfully.`,
          }),
        );
        dispatch({ type: MODAL_STATE, status: 'closed' });
        onModalClose();
        navigate(DASHBOARD_FULL);
      })
      .catch((error) => {
        const formattedError = error.replace('<![CDATA[', '').replace(']]>', '');
        dispatch(
          notifyWithSnack({
            message: `Error updating profile: ${formattedError}`,
            type: 'error',
          }),
        );
      });
  };

  const toggleShowNamePopup = () => {
    setShowNamePopup((prevShowNamePopup) => !prevShowNamePopup);
    setShowPassPopup(false);
  };

  const toggleShowPassPopup = () => {
    setShowPassPopup((prevShowPassPopup) => !prevShowPassPopup);
    setShowNamePopup(false);
  };

  const shouldShowNameForm = !first_name || !last_name || !email;
  const shouldShowPasswordForm = isPasswordValidationFailed;

  // If name or password forms are needed, show them with FormProvider
  return (
    <FormProvider {...formMethods}>
      <div className={cx('modal-header', '')}>
        <Typography className={cx('title')} type="h2">
          Welcome Back!
        </Typography>
        {shouldShowNameForm && (
          <Typography className={cx('libre', '')} type="body-s">
            To use the new Dashboard feature, you must update your account with your name and email.
          </Typography>
        )}
        {shouldShowPasswordForm && (
          <Typography className={cx('libre', '')} type="body-s">
            Your current password does not meet our new security requirements and needs updating.
          </Typography>
        )}
      </div>
      <form className={cx('form-container', 'mgt-20')} onSubmit={formMethods.handleSubmit(handleFormSubmit)}>
        <div className={cx('columns')}>
          {shouldShowNameForm && (
            <div className={cx('name-form')}>
              <NameForm />
            </div>
          )}
          {shouldShowPasswordForm && (
            <div className={cx('password-form')}>
              <PasswordForm />
            </div>
          )}
        </div>
        <div className={cx('buttons-wrapper', 'mgt-10')}>
          {shouldShowNameForm && (
            <>
              <Button
                label="Why do we need your name?"
                onClick={toggleShowNamePopup}
                type="button"
                variant="link"
              />
              {showNamePopup && (
                <div className={cx('popup')}>
                  <Typography className={cx('popup-text')}>
                    <span>Why do we need your name?</span>
                    The Dashboard creates student accounts for you that are linked to your teacher account. The
                    student usernames that are created are based on your last name and first initial.
                  </Typography>
                </div>
              )}
              <Button
                label="Why do we need your email?"
                onClick={toggleShowPassPopup}
                type="button"
                variant="link"
              />
              {showPassPopup && (
                <div className={cx('popup')}>
                  <Typography className={cx('popup-text')}>
                    <span>Why do we need your email?</span>
                    Your email will be used for account security — a way that we can contact you with any issues,
                    including confirming with you each year that you plan to continue using Mission US under your
                    school's Data Privacy Agreement, if applicable.
                  </Typography>
                </div>
              )}
            </>
          )}
        </div>
        <Button className="btn-submit--absolute mgt-30" label="Submit" submit />
      </form>
    </FormProvider>
  );
};

export default ProfileForm;
